import { ArrowRightIcon, CheckIcon, PlusIcon } from '@heroicons/react/outline';
import Head from 'next/head';
import NextImage from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

import styles from './finishing.module.css';

import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

import { VIDEO_TOOL_ROUTES } from '@/constants/videoTools';

import type { FinishingLandingProps } from './types';

const QUESTIONS = {
  upscale: [
    [
      'What does an AI video upscaler do?',
      'It processes an existing video to increase its resolution and improve perceived clarity. Choose a target such as 1080p, 2K, or 4K, and download the processed video as a separate file.',
    ],
    [
      'Can I upscale a video without generating it again?',
      'Yes. Standard, Pro, and Ultimate process your existing footage without a new prompt. For eligible MiniMax H3 videos generated at 768p in Easy-Peasy.AI, the native 2K upgrade reuses the original video, prompt, and references.',
    ],
    [
      'Which video files can I upload?',
      'Upload an MP4 or MOV file up to 100 MB and 10 minutes long, or choose a completed video from your Easy-Peasy.AI gallery. Native MiniMax upgrades have separate eligibility requirements.',
    ],
    [
      'Which upscaling model should I choose?',
      'Standard is the lowest-cost option. Pro focuses on finer detail, and Ultimate targets textures and temporal consistency. Start with a short clip when comparing models: the best result depends on your source footage.',
    ],
    [
      'Will upscaling fix every blurry video?',
      'No. Results depend on the source quality. AI can improve perceived detail, but it cannot guarantee faithful recovery of information that was never captured. Faces, small text, and heavily compressed footage deserve a close check.',
    ],
    [
      'Can I upgrade MiniMax H3 Reference from 768p to 2K?',
      'Yes, when the original H3 video and its generation inputs are available in your account. Open the saved video menu and choose “Upgrade to 2K · MiniMax”. H3 Max and videos already generated at 2K are not eligible for this native upgrade.',
    ],
    [
      'Is video upscaling free?',
      'Video upscaling requires a paid Easy-Peasy.AI plan and uses credits. The cost depends on the model, video length, and output resolution. You see the credit total before submitting.',
    ],
    [
      'How long does video upscaling take?',
      'Processing time varies with video length, resolution, and model load. Short clips may finish in minutes; longer clips, especially with advanced models, can take hours. Your result appears in your video gallery when it is ready.',
    ],
  ],
  extend: [
    [
      'What does an AI video extender do?',
      'It generates a continuation for an existing video based on your prompt. Describe what happens next, choose the length of the new segment, and receive a longer video. This adds new footage rather than simply slowing down or looping the original.',
    ],
    [
      'How much longer can I make my video?',
      'Seedance 2.0, Fast, and Mini add 4–15 seconds per run. Seedance 2.5 supports selected lengths up to 30 seconds. Gemini Omni Flash 1.1 adds 3–10 seconds. Available choices are shown in the workspace.',
    ],
    [
      'Which source videos are supported?',
      'Upload MP4 or MOV. Seedance 2.0 tiers accept source clips up to 15 seconds and 100 MB; Seedance 2.5 accepts up to 30 seconds and 100 MB. Gemini Omni Flash 1.1 accepts up to 10 seconds and 20 MB. Sources must be at least 2 seconds long.',
    ],
    [
      'Will my original video stay unchanged?',
      'Your original saved file is kept, and the extended result is a separate video. Seedance appends a continuation from the last frame. Gemini Omni uses more of the source as context and can re-render it, so review the whole result when exact preservation matters.',
    ],
    [
      'Can I extend the same video more than once?',
      'Yes, as long as the latest result still fits the selected model’s source-length and file-size limits. Choose another supported model or trim the input when a clip exceeds those limits.',
    ],
    [
      'What should I write in the extension prompt?',
      'Describe the next action and how the camera should move. Keep the scene, subject, and lighting consistent for a natural continuation. For example: “The camera slowly pulls back as the dancer finishes her turn; keep the warm stage lighting.”',
    ],
    [
      'Does extending a video also increase its resolution?',
      'The models offer output-resolution choices, including up to 4K. For improving the resolution of the existing video without adding a scene, use the dedicated AI Video Upscaler.',
    ],
    [
      'How are extension credits calculated?',
      'The cost depends on the model, added length, and output resolution. Seedance 2.0 tiers bill the added segment. Seedance 2.5 and Gemini Omni also bill the source duration. A paid plan is required, and the workspace shows the total before you start.',
    ],
  ],
} as const;

export default function VideoFinishingLanding({
  kind,
  models,
  nativeExampleCredits,
}: FinishingLandingProps) {
  const { t: translate } = useTranslation('video-finishing');
  const { locale = 'en' } = useRouter();
  const t = (key: string, values?: Record<string, string | number>) =>
    translate(key, values, { default: key, ns: 'video-finishing' });
  const upscale = kind === 'upscale';
  const route = VIDEO_TOOL_ROUTES[kind];
  const [selectedModel, setSelectedModel] = useState(models[0].id);
  const model = models.find((item) => item.id === selectedModel) || models[0];
  const [resolution, setResolution] = useState(upscale ? '4k' : '720p');
  const [seconds, setSeconds] = useState(upscale ? 10 : 5);
  const [extraSeconds, setExtraSeconds] = useState(5);
  const [showSample, setShowSample] = useState(false);
  const currentResolution = model.resolutions.includes(resolution)
    ? resolution
    : model.resolutions[0];
  const currentSeconds = model.durations.includes(seconds)
    ? seconds
    : model.durations[0];
  const credits = model.examplePrices[currentResolution][currentSeconds];
  const name = upscale ? 'AI Video Upscaler' : 'AI Video Extender';
  const title = upscale
    ? 'AI Video Upscaler — Upscale Videos to 1080p, 2K & 4K'
    : 'AI Video Extender — Continue Videos with AI';
  const description = upscale
    ? 'Upscale videos to 1080p, 2K, or 4K. Compare Standard, Pro, Ultimate, and native MiniMax H3 upgrades. Preview credit costs and enhance your existing footage online.'
    : 'Extend videos with AI-generated scenes. Compare Seedance and Gemini Omni, add up to 30 seconds per run, and preview credit costs before continuing your story.';
  const poster = upscale
    ? 'https://media.easy-peasy.ai/landing/home/vid-butterfly-poster.jpg'
    : 'https://media.easy-peasy.ai/landing/video/hero2-poster.jpg';
  const sample = upscale
    ? 'https://media.easy-peasy.ai/landing/home/vid-butterfly.mp4'
    : 'https://media.easy-peasy.ai/landing/video/hero2.mp4';
  const prefix = locale === 'en' ? '' : `/${locale}`;
  const canonical = `https://easy-peasy.ai${prefix}${route.landing}`;
  const cta = upscale ? t('Upscale your video') : t('Extend your video');
  const questions = QUESTIONS[kind];
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonical}#page`,
        url: canonical,
        name: t(title),
        description: t(description),
        inLanguage: locale,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Easy-Peasy.AI',
            item: `https://easy-peasy.ai${prefix}`,
          },
          { '@type': 'ListItem', position: 2, name: t(name), item: canonical },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: questions.map(([q, a]) => ({
          '@type': 'Question',
          name: t(q),
          acceptedAnswer: { '@type': 'Answer', text: t(a) },
        })),
      },
    ],
  };
  return (
    <div
      className={styles.page}
      lang={locale}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <Seo
        title={`${t(title)} | Easy-Peasy.AI`}
        description={t(description)}
        image={`https://easy-peasy.ai/api/og-template?title=${encodeURIComponent(t(name))}`}
        page='other'
        structuredData={false}
      />
      <Head>
        <script
          key='video-finishing-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <a href='#main-content' className={styles.skip}>
        {t('Skip to content')}
      </a>
      <header className={styles.header}>
        <LandingPageNavigation />
      </header>
      <main id='main-content'>
        <section className={`${styles.hero} ${styles.container}`}>
          <div className={styles.heroCopy}>
            <nav aria-label={t('Breadcrumb')} className={styles.breadcrumb}>
              <Link href='/ai-video-generator'>{t('Video studio')}</Link>
              <span aria-hidden='true'>/</span>
              <span>{t(upscale ? 'Upscale' : 'Extend')}</span>
            </nav>
            <h1>
              <span className={styles.eyebrow}>{t(name)}</span>
              <span className={styles.heroTitle}>
                {t(upscale ? 'Your video.' : 'The story')}
                <br />
                <em>{t(upscale ? 'A sharper finish.' : 'keeps going.')}</em>
              </span>
            </h1>
            <p className={styles.lead}>
              {t(
                upscale
                  ? 'Keep the moment. Bring out the detail. Turn your existing footage into a higher-resolution video, ready for its next screen.'
                  : 'A great shot deserves a little more time. Continue your video with new motion, a longer scene, or the ending you imagined.',
              )}
            </p>
            <div className={styles.actions}>
              <Link href={route.workspace} className={styles.primary}>
                {cta}
                <ArrowRightIcon aria-hidden='true' />
              </Link>
              <a href='#models' className={styles.textLink}>
                {t('Find your model')}
                <span aria-hidden='true'>↗</span>
              </a>
            </div>
            <p className={styles.finePrint}>
              {t(
                'Paid plans · Credits shown before you start · Original file kept',
              )}
            </p>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.previewTop}>
              <span className={styles.liveDot} />
              {t(
                upscale
                  ? 'A closer look at every frame'
                  : 'One shot. A little more possibility.',
              )}
              <span className={styles.previewIndex}>
                {upscale ? '01 / 02' : '02 / 02'}
              </span>
            </div>
            <div className={styles.film}>
              {showSample ? (
                <video
                  src={sample}
                  poster={poster}
                  controls
                  autoPlay
                  muted
                  playsInline
                  preload='none'
                  className={styles.filmMedia}
                />
              ) : (
                <>
                  <NextImage
                    src={poster}
                    alt={t(
                      upscale
                        ? 'A blue butterfly on a fern with dew drops in warm sunlight'
                        : 'A ballet dancer turning under a warm stage spotlight',
                    )}
                    width={upscale ? 640 : 960}
                    height={upscale ? 360 : 540}
                    loading='eager'
                    sizes='(max-width: 760px) calc(100vw - 40px), (max-width: 1304px) 48vw, 620px'
                    fetchPriority='high'
                    className={styles.filmMedia}
                  />
                  <button
                    className={styles.play}
                    onClick={() => setShowSample(true)}
                    aria-label={t('Play example footage')}
                  >
                    <span aria-hidden='true'>▶</span>
                  </button>
                </>
              )}
              <span className={styles.filmLabel}>{t('Example footage')}</span>
            </div>
            {upscale ? (
              <div className={styles.resolutionStrip}>
                <span>{t('Choose your finish')}</span>
                <div>
                  {['720p', '1080p', '2k', '4k'].map((value) => (
                    <button
                      key={value}
                      aria-pressed={resolution === value}
                      onClick={() => setResolution(value)}
                    >
                      {value.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className={styles.timeline}>
                <div className={styles.timelineLabels}>
                  <span>{t('Your clip')} · 5s</span>
                  <span>
                    {t('New continuation')} · +{extraSeconds}s
                  </span>
                </div>
                <div className={styles.timelineTrack}>
                  <span className={styles.originalTrack} />
                  <span
                    className={styles.extensionTrack}
                    style={{ flexGrow: extraSeconds / 5 }}
                  >
                    {t('What happens next?')}
                  </span>
                </div>
                <label htmlFor='timeline-preview'>
                  {t('Explore the added length')}
                  <strong>+{extraSeconds}s</strong>
                </label>
                <input
                  id='timeline-preview'
                  type='range'
                  min='4'
                  max='15'
                  value={extraSeconds}
                  onChange={(event) =>
                    setExtraSeconds(Number(event.target.value))
                  }
                />
                <p>
                  {t(
                    'Timeline illustration. Actual results vary by prompt and model.',
                  )}
                </p>
              </div>
            )}
            <div className={styles.visualNote}>
              <span aria-hidden='true'>↗</span>
              <span>
                {t(
                  upscale
                    ? 'More room for the details that matter.'
                    : 'Start with your clip. Tell AI what comes next.',
                )}
              </span>
            </div>
          </div>
        </section>
        <div className={styles.promiseBand}>
          <div className={styles.container}>
            {(upscale
              ? [
                  'Up to 4K output',
                  'Standard, Pro & Ultimate',
                  'Native MiniMax H3 upgrade',
                  'MP4 & MOV uploads',
                ]
              : [
                  'Add up to 30 seconds',
                  'Seedance & Gemini Omni',
                  'Describe the next scene',
                  'A separate finished video',
                ]
            ).map((value) => (
              <span key={value}>
                <CheckIcon aria-hidden='true' />
                {t(value)}
              </span>
            ))}
          </div>
        </div>
        <section className={`${styles.container} ${styles.intro}`}>
          <div>
            <span className={styles.eyebrow}>
              {t('Made for the footage you already have')}
            </span>
            <h2>
              {t(
                upscale
                  ? 'The same moment.\nMore potential.'
                  : 'Keep the take\nworth keeping.',
              )}
            </h2>
          </div>
          <div className={styles.editorialList}>
            {(upscale
              ? [
                  [
                    'Give AI videos a final pass',
                    'Happy with the composition and motion? Upscale the clip you already like instead of starting a new generation.',
                  ],
                  [
                    'Prepare for a bigger screen',
                    'Move from a small preview to 1080p, 2K, or 4K output for presentations, social campaigns, and your next edit.',
                  ],
                  [
                    'Choose where to spend your credits',
                    'Use Standard for an economical pass, then compare Pro or Ultimate on shots that need more attention.',
                  ],
                ]
              : [
                  [
                    'Let a scene breathe',
                    'Give a product shot, establishing scene, or character moment a little more time before the next cut.',
                  ],
                  [
                    'Direct what happens next',
                    'Describe the action, camera movement, and mood in plain language. Keep the prompt focused for a more natural continuation.',
                  ],
                  [
                    'Build a longer story, one shot at a time',
                    'Continue from an eligible result, or trim a longer clip to fit your chosen model’s source window.',
                  ],
                ]
            ).map(([heading, body], i) => (
              <article key={heading}>
                <span className={styles.number}>0{i + 1}</span>
                <div>
                  <h3>{t(heading)}</h3>
                  <p>{t(body)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section id='models' className={styles.modelsSection}>
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <div>
                <span className={styles.eyebrow}>
                  {t('Your footage. Your choice.')}
                </span>
                <h2>
                  {t(
                    upscale
                      ? 'Find the right upscaler.'
                      : 'Choose your next scene partner.',
                  )}
                </h2>
              </div>
              <p>
                {t(
                  upscale
                    ? 'Three ways to improve an existing clip, plus a native upgrade for eligible MiniMax H3 videos.'
                    : 'Balance source length, creative control, and credit cost. Every option opens in the same focused workspace.',
                )}
              </p>
            </div>
            <div className={styles.modelList}>
              {models.map((item, i) => (
                <article
                  key={item.id}
                  className={`${styles.modelRow} ${item.id === model.id ? styles.selectedRow : ''}`}
                >
                  <div className={styles.modelName}>
                    <span className={styles.modelNumber}>0{i + 1}</span>
                    <h3>
                      <bdi>{item.name}</bdi>
                    </h3>
                    {i === 0 && (
                      <span className={styles.modelTag}>
                        {t(upscale ? 'Lowest cost' : 'Start here')}
                      </span>
                    )}
                  </div>
                  <p>{t(item.description)}</p>
                  <div className={styles.modelFacts}>
                    <span>
                      {upscale
                        ? t('Up to 4K')
                        : `${Math.min(...item.durations)}–${Math.max(...item.durations)}s ${t('added')}`}
                    </span>
                    <span>
                      {upscale
                        ? t('Up to 10 min · 100 MB')
                        : `${item.sourceMax}s ${t('source')} · ${item.sourceMB} MB`}
                    </span>
                  </div>
                  <button
                    className={styles.compareButton}
                    aria-pressed={model.id === item.id}
                    onClick={() => {
                      setSelectedModel(item.id);
                      document
                        .getElementById('credit-preview')
                        ?.scrollIntoView({
                          behavior: 'instant',
                          block: 'center',
                        });
                    }}
                  >
                    {t('Preview cost')}
                    <span aria-hidden='true'>↗</span>
                  </button>
                </article>
              ))}
            </div>
            <div id='credit-preview' className={styles.costPanel}>
              <div>
                <span className={styles.eyebrow}>
                  {t('No guesswork at checkout')}
                </span>
                <h3>{t('See an example credit cost')}</h3>
                <p>
                  {t(
                    upscale
                      ? 'Choose a clip length and resolution to compare the models.'
                      : 'This example starts with a 5-second source clip. Choose how much new footage to add.',
                  )}
                </p>
              </div>
              <div className={styles.costControls}>
                <label>
                  <span id='cost-model-label'>{t('Model')}</span>
                  <select
                    aria-labelledby='cost-model-label'
                    value={model.id}
                    onChange={(event) => setSelectedModel(event.target.value)}
                  >
                    {models.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span id='cost-length-label'>
                    {t(upscale ? 'Video length' : 'Added length')}
                  </span>
                  <select
                    aria-labelledby='cost-length-label'
                    value={currentSeconds}
                    onChange={(event) => setSeconds(Number(event.target.value))}
                  >
                    {model.durations.map((value) => (
                      <option key={value} value={value}>
                        {value}s
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span id='cost-resolution-label'>{t('Resolution')}</span>
                  <select
                    aria-labelledby='cost-resolution-label'
                    value={currentResolution}
                    onChange={(event) => setResolution(event.target.value)}
                  >
                    {model.resolutions.map((value) => (
                      <option key={value} value={value}>
                        {value.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className={styles.costTotal} aria-live='polite'>
                <strong>{credits}</strong>
                <span>{t('credits')}</span>
                <small>
                  {t(
                    model.billsSource
                      ? 'Includes the 5-second source'
                      : upscale
                        ? 'For the selected video length'
                        : 'For the new segment',
                  )}
                </small>
              </div>
            </div>
            <p className={styles.costFootnote}>
              {t(
                upscale
                  ? 'Standard, Pro, and Ultimate round video length up to whole seconds, with a 3-second minimum. The final quote uses your uploaded file.'
                  : 'Seedance 2.5 and Gemini Omni bill source + added seconds. Seedance 2.0 tiers bill the new segment only. The final quote uses your uploaded file.',
              )}{' '}
              <Link href='/pricing'>{t('Explore paid plans')} →</Link>
            </p>
          </div>
        </section>
        {upscale && (
          <section className={`${styles.container} ${styles.nativeSection}`}>
            <div className={styles.nativeMark} aria-hidden='true' dir='ltr'>
              <span>768p</span>
              <span>↗</span>
              <strong>2K</strong>
            </div>
            <div>
              <span className={styles.eyebrow}>
                {t('Already love your MiniMax result?')}
              </span>
              <h2>{t('Keep your H3 creation.\nGive it the 2K treatment.')}</h2>
              <p>
                {t(
                  'Generated at 768p with MiniMax H3, H3 Image, or H3 Reference? Use the native upgrade from your saved video menu. It reuses the original video, prompt, and references, then saves a separate 2K result.',
                )}
              </p>
              <p className={styles.nativeDetails}>
                {t(
                  'A 5-second example with no extra reference charges uses {{credits}} credits.',
                  { credits: nativeExampleCredits },
                )}{' '}
                {t(
                  'Original reference videos and images beyond the first five are billed again. H3 Max and already-2K videos are not eligible.',
                )}
              </p>
              <Link href='/upscale-video#videos' className={styles.textLink}>
                {t('Open your saved videos')}
                <ArrowRightIcon aria-hidden='true' />
              </Link>
            </div>
          </section>
        )}
        <section
          id='how-it-works'
          className={`${styles.container} ${styles.steps}`}
        >
          <div className={styles.sectionHeading}>
            <div>
              <span className={styles.eyebrow}>
                {t('From upload to final cut')}
              </span>
              <h2>{t('Three steps. One better video.')}</h2>
            </div>
            <Link href={route.workspace} className={styles.textLink}>
              {cta}
              <ArrowRightIcon aria-hidden='true' />
            </Link>
          </div>
          <ol>
            {[
              [
                t('Bring your video'),
                t(
                  upscale
                    ? 'Upload an MP4 or MOV up to 100 MB and 10 minutes, or choose a completed clip from your gallery.'
                    : 'Upload an MP4 or MOV, or use a saved clip. Check the source-length and file-size limits for your model.',
                ),
              ],
              [
                t(upscale ? 'Choose your finish' : 'Describe the next moment'),
                t(
                  upscale
                    ? 'Pick your upscaler and output resolution. Review the credit cost before you start.'
                    : 'Write a continuation prompt, choose added length and resolution, and review the credit total.',
                ),
              ],
              [
                t('Save the new result'),
                t(
                  'Your video processes in the background. Find the finished file in your gallery, ready to preview, download, or use in your next edit.',
                ),
              ],
            ].map(([heading, body], i) => (
              <li key={heading}>
                <span className={styles.stepNumber}>0{i + 1}</span>
                <h3>{heading}</h3>
                <p>{body}</p>
              </li>
            ))}
          </ol>
        </section>
        <section className={styles.faqSection}>
          <div className={`${styles.container} ${styles.faqLayout}`}>
            <div>
              <span className={styles.eyebrow}>
                {t('A few things worth knowing')}
              </span>
              <h2>{t('Before you\nhit render.')}</h2>
              <p>
                {t(
                  'Straight answers to help you choose the right tool for your video.',
                )}
              </p>
              <Link href='/contact' className={styles.textLink}>
                {t('Talk to our team')} →
              </Link>
            </div>
            <div>
              {questions.map(([question, answer]) => (
                <details key={question}>
                  <summary>
                    {t(question)}
                    <PlusIcon aria-hidden='true' />
                  </summary>
                  <p>{t(answer)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
        <section className={`${styles.container} ${styles.finalCta}`}>
          <span className={styles.eyebrow}>
            {t(
              upscale
                ? 'Finish what you started'
                : 'There is more to the story',
            )}
          </span>
          <h2>
            {t(
              upscale
                ? 'Make your next frame\na little more remarkable.'
                : 'Your best scene\nmight be the next one.',
            )}
          </h2>
          <Link href={route.workspace} className={styles.primary}>
            {cta}
            <ArrowRightIcon aria-hidden='true' />
          </Link>
          <p>
            {t(
              'A dedicated workspace. A clear credit quote. Your original file kept.',
            )}
          </p>
        </section>
        <nav
          aria-label={t('Related video tools')}
          className={`${styles.container} ${styles.related}`}
        >
          <Link
            href={VIDEO_TOOL_ROUTES[upscale ? 'extend' : 'upscale'].landing}
          >
            <span>
              {t(
                upscale
                  ? 'Need a longer scene?'
                  : 'Ready for a sharper finish?',
              )}
            </span>
            <strong>
              {t(
                upscale
                  ? 'Explore AI Video Extender'
                  : 'Explore AI Video Upscaler',
              )}{' '}
              ↗
            </strong>
          </Link>
          <Link href='/ai-video-generator'>
            <span>{t('Start something new')}</span>
            <strong>{t('Explore AI Video Generator')} ↗</strong>
          </Link>
          <Link href='/ai-video-editor'>
            <span>{t('Put the story together')}</span>
            <strong>{t('Open AI Video Editor')} ↗</strong>
          </Link>
        </nav>
      </main>
      <Footer />
    </div>
  );
}
